import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { isClientUser } from '@src/clientUser/useClientUser';
import { AddButton } from '@src/common/components/button/AddButton';
import { CloseConfirm } from '@src/common/components/CloseConfirm';
import { Stack } from '@src/common/components/Stack';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { AddNoteForm } from '@src/notes/AddNoteForm';
import { AddNoteSchema } from '@src/notes/AddNoteSchema';
import { NoteContext } from '@src/notes/domain/NoteContext';
import { useAddNote } from '@src/notes/hooks/useAddNote';
import { NoteContentField, NoteContextField } from '@src/notes/NoteFields';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

export type ParentType = 'ORDER' | 'PERMIT_APPLICATION';
export type AddNoteComponentProps = {
  parentId: string;
  parentType: ParentType;
  isInitiallyAddingNote: boolean;
  onAddNote: () => void;
  defaultNoteContext?: NoteContext[];
};

export const AddNote = (props: AddNoteComponentProps) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();
  const { showSuccessToast, showErrorToast } = useToast();

  const [isAddingNote, setIsAddingNote] = useState(props.isInitiallyAddingNote);

  const noteContextValues = isClientUser(user)
    ? [NoteContext.client.valueOf()]
    : props.defaultNoteContext?.map((context) => context.valueOf());

  //oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const form = useForm<yup.InferType<typeof AddNoteSchema> | any>({
    resolver: yupResolver(AddNoteSchema),
    defaultValues: noteContextValues
      ? { [NoteContextField.fieldName]: noteContextValues }
      : undefined,
  });

  const { addNote } = useAddNote({
    onSuccess: () => {
      showSuccessToast(t('notes.add.success'));
      setIsAddingNote(false);
      props.onAddNote();
    },
    onError: () => showErrorToast(t('notes.add.error')),
  });

  const confirmAddNote: SubmitHandler<yup.InferType<typeof AddNoteSchema>> = (
    data,
  ) => {
    addNote({
      author: user.id,
      tags: data[NoteContextField.fieldName],
      content: data[NoteContentField.fieldName],
      parent: { id: props.parentId, type: props.parentType },
      sendByMail: data.sendNoteByMail,
    });
    form.reset();
  };

  return (
    <Stack gap={theme.spacing['6']}>
      {isAddingNote && (
        <AddNoteForm
          control={form.control}
          setValue={form.setValue}
          context={isClientUser(user) ? NoteContext.client : undefined}
        />
      )}
      {!isAddingNote && (
        <Stack
          width="fit-content"
          alignSelf="flex-end"
          marginY={theme.spacing['6']}
        >
          <AddButton
            onClick={() => setIsAddingNote(true)}
            label={t('notes.add.title')}
          />
        </Stack>
      )}
      {isAddingNote && (
        <CloseConfirm
          onClose={() => {
            setIsAddingNote(false);
            form.reset();
          }}
          confirm={form.handleSubmit(confirmAddNote)}
          closeLabel={t('cancel')}
        />
      )}
    </Stack>
  );
};
